// AUTO-GENERATED from the Vocable MCP server's registered tools.
// Source of truth: dnadml/vocable-mcp-server src/tools + src/resources.
// Descriptions are the server's own, minus the model-directed confirmation and
// plan sentences, which are lifted into requiresConfirmation / plan for badges.
// Regenerate rather than hand-editing, so the docs cannot drift from the server.

export const MCP_ENDPOINT = 'https://mcp.vocable.ai/mcp/v1';

export const TOOL_CATEGORIES = [
  {
    "id": "brands",
    "label": "Brands",
    "blurb": "Pick which brand the agent works on. Every other tool is scoped to it.",
    "tools": [
      {
        "name": "get_brand_context",
        "description": "Get ALL brand intelligence data in one call \u2014 profile, voice DNA, content guidelines, audience personas, content strategy, content samples, and social channels. Use this whenever you need full brand context for writing, strategizing, or advising. This gives you everything needed to write on-brand content, even outside of Vocable's tools.",
        "requiresConfirmation": false,
        "plan": null,
        "params": []
      },
      {
        "name": "list_brands",
        "description": "List all brands available to you. If you have multiple brands, use set_default_brand to select one before using other tools.",
        "requiresConfirmation": false,
        "plan": null,
        "params": []
      },
      {
        "name": "set_default_brand",
        "description": "Set the active brand for all subsequent operations. Use list_brands first to see available brands and their IDs.",
        "requiresConfirmation": false,
        "plan": null,
        "params": [
          {
            "name": "brand_profile_id",
            "type": "number",
            "required": true,
            "description": "The brand profile ID to use as the active brand",
            "default": null
          }
        ]
      }
    ]
  },
  {
    "id": "content",
    "label": "Content generation",
    "blurb": "Draft, ideate and health-check content in your brand voice.",
    "tools": [
      {
        "name": "check_document_health",
        "description": "Analyze a document's content quality \u2014 readability, SEO, engagement metrics, word count, reading time, and AI detection score. Returns improvement suggestions.",
        "requiresConfirmation": false,
        "plan": null,
        "params": [
          {
            "name": "document_id",
            "type": "string",
            "required": true,
            "description": "The slug ID of the document to analyze",
            "default": null
          },
          {
            "name": "force",
            "type": "boolean",
            "required": false,
            "description": "Force recalculation even if a cached score exists",
            "default": false
          }
        ]
      },
      {
        "name": "generate_brief",
        "description": "Generate an AI-powered content brief with structured fields \u2014 description, objective, keywords, content structure, target audience, and suggested word count.",
        "requiresConfirmation": false,
        "plan": null,
        "params": [
          {
            "name": "content_type",
            "type": "string (Blog Post, Youtube Script, Podcast Episode, Case Study, Quiz, Guide)",
            "required": true,
            "description": "Type of content to brief for",
            "default": null
          },
          {
            "name": "topic",
            "type": "string",
            "required": true,
            "description": "The topic or headline for the content brief",
            "default": null
          },
          {
            "name": "goals",
            "type": "string",
            "required": false,
            "description": "Goals for the content (e.g., 'drive traffic', 'thought leadership')",
            "default": null
          },
          {
            "name": "keywords",
            "type": "string",
            "required": false,
            "description": "Comma-separated keywords to target",
            "default": null
          },
          {
            "name": "supporting_context",
            "type": "string",
            "required": false,
            "description": "Additional context or reference material",
            "default": null
          },
          {
            "name": "target_audience",
            "type": "string",
            "required": false,
            "description": "Target audience description",
            "default": null
          }
        ]
      },
      {
        "name": "generate_content",
        "description": "Generate content for a brief using Vocable's AI engine \u2014 same as clicking 'View Content' in the Vocable web app. Pass a task_id from list_briefs to generate content for an existing brief. Content is generated by Vocable's AI (not Claude), using brand voice, style guide, and RAG context. The document is created and linked to the brief so it appears in the Vocable UI.",
        "requiresConfirmation": true,
        "plan": null,
        "params": [
          {
            "name": "content_type",
            "type": "string (Blog Post, Youtube Script, Podcast Episode, Case Study, Quiz, Guide)",
            "required": true,
            "description": "Type of content to generate",
            "default": null
          },
          {
            "name": "title",
            "type": "string",
            "required": true,
            "description": "Content title or headline",
            "default": null
          },
          {
            "name": "custom_instructions",
            "type": "string",
            "required": false,
            "description": "Additional instructions for Vocable's AI",
            "default": null
          },
          {
            "name": "description",
            "type": "string",
            "required": false,
            "description": "What the content should cover",
            "default": null
          },
          {
            "name": "keywords",
            "type": "array<string>",
            "required": false,
            "description": "Target keywords to include",
            "default": null
          },
          {
            "name": "language",
            "type": "string",
            "required": false,
            "description": "Language code (e.g., en-US, fr, es, de)",
            "default": "en-US"
          },
          {
            "name": "project_id",
            "type": "string",
            "required": false,
            "description": "Campaign project_id \u2014 uses the campaign's brand context",
            "default": null
          },
          {
            "name": "target_audience",
            "type": "string",
            "required": false,
            "description": "Who this content is for",
            "default": null
          },
          {
            "name": "task_id",
            "type": "number",
            "required": false,
            "description": "Existing brief task_id from list_briefs. Links the generated document to this brief.",
            "default": null
          },
          {
            "name": "word_count",
            "type": "number",
            "required": false,
            "description": "Target word count (approximate)",
            "default": null
          }
        ]
      },
      {
        "name": "ideate_content",
        "description": "Generate 10 content ideas based on a prompt. Returns titles, descriptions, and suggested content types for each idea.",
        "requiresConfirmation": false,
        "plan": null,
        "params": [
          {
            "name": "prompt",
            "type": "string",
            "required": true,
            "description": "Describe the kind of content ideas you want (e.g., 'content ideas for a SaaS startup selling project management tools')",
            "default": null
          }
        ]
      }
    ]
  },
  {
    "id": "documents",
    "label": "Documents",
    "blurb": "Create, read, update and publish documents.",
    "tools": [
      {
        "name": "create_document",
        "description": "Create a new document, optionally linked to a task, folder, or campaign.",
        "requiresConfirmation": false,
        "plan": null,
        "params": [
          {
            "name": "title",
            "type": "string",
            "required": true,
            "description": "Document title",
            "default": null
          },
          {
            "name": "content",
            "type": "string",
            "required": false,
            "description": "HTML content of the document",
            "default": ""
          },
          {
            "name": "folder_id",
            "type": "string",
            "required": false,
            "description": "Slug of the folder to place the document in",
            "default": null
          },
          {
            "name": "project_id",
            "type": "string",
            "required": false,
            "description": "Campaign/project slug to associate with",
            "default": null
          },
          {
            "name": "status",
            "type": "string (not_started, in_progress, in_review, approved)",
            "required": false,
            "description": "Initial workflow status",
            "default": "in_progress"
          },
          {
            "name": "task_id",
            "type": "number",
            "required": false,
            "description": "Link to an existing content brief (task)",
            "default": null
          },
          {
            "name": "template_id",
            "type": "string",
            "required": false,
            "description": "Content template ID to apply",
            "default": null
          }
        ]
      },
      {
        "name": "get_document",
        "description": "Get a document's full content, metadata, and collaborators by its slug ID.",
        "requiresConfirmation": false,
        "plan": null,
        "params": [
          {
            "name": "document_id",
            "type": "string",
            "required": true,
            "description": "The slug ID of the document",
            "default": null
          }
        ]
      },
      {
        "name": "get_publish_settings",
        "description": "Get existing publish settings for a document on a specific platform integration. Returns story_detail (title, excerpt, slug, author, categories, tags), seo_setting (meta_title, meta_description, focus_keyword), and publish_settings (status, scheduled_at). Use this before publish_document to check what is already configured. Use list_integrations first to get the integration_id.",
        "requiresConfirmation": false,
        "plan": null,
        "params": [
          {
            "name": "document_id",
            "type": "string",
            "required": true,
            "description": "The slug ID of the document",
            "default": null
          },
          {
            "name": "integration_id",
            "type": "number",
            "required": true,
            "description": "ID of the platform integration (from list_integrations)",
            "default": null
          }
        ]
      },
      {
        "name": "list_documents",
        "description": "List documents (the actual written content, NOT briefs). Documents contain the generated or written content like blog posts, LinkedIn posts, etc. To list content briefs/plans for a campaign, use list_tasks instead. Supports filtering by type, status, project, and search.",
        "requiresConfirmation": false,
        "plan": null,
        "params": [
          {
            "name": "page",
            "type": "number",
            "required": false,
            "description": "Page number",
            "default": 1
          },
          {
            "name": "project_id",
            "type": "string",
            "required": false,
            "description": "Scope to a specific campaign/project slug",
            "default": null
          },
          {
            "name": "search",
            "type": "string",
            "required": false,
            "description": "Search documents by title (partial match)",
            "default": null
          },
          {
            "name": "status",
            "type": "string (all, not_started, in_progress, in_review, approved, published)",
            "required": false,
            "description": "Filter by workflow status",
            "default": "all"
          },
          {
            "name": "type",
            "type": "string (all, drafts, shared, starred, trash, individual)",
            "required": false,
            "description": "Filter by relationship type",
            "default": "all"
          }
        ]
      },
      {
        "name": "publish_document",
        "description": "Publish or schedule a document to a connected platform (WordPress, Webflow, Wix, LinkedIn, X, Facebook). Before calling this tool, use list_integrations to show the user their connected platforms and ask which one they want to publish to. For blog platforms (WordPress, Wix), you should also ask about story details (title, excerpt, slug, author, categories, tags) and SEO settings. Use get_publish_settings first to check if settings are already configured for this document. This tool first saves platform integration settings, then triggers the publish.",
        "requiresConfirmation": true,
        "plan": "Pro+",
        "params": [
          {
            "name": "document_id",
            "type": "string",
            "required": true,
            "description": "The slug ID of the document to publish",
            "default": null
          },
          {
            "name": "integration_id",
            "type": "number",
            "required": true,
            "description": "ID of the platform integration to publish to (from list_integrations)",
            "default": null
          },
          {
            "name": "author_id",
            "type": [
              "string",
              "number"
            ],
            "required": false,
            "description": "Author ID on the target platform (use get_publish_settings or list_integrations to find available authors)",
            "default": null
          },
          {
            "name": "category_ids",
            "type": "array<['string', 'number']>",
            "required": false,
            "description": "Array of category IDs on the target platform",
            "default": null
          },
          {
            "name": "focus_keyword",
            "type": "string",
            "required": false,
            "description": "Primary focus keyword for SEO",
            "default": null
          },
          {
            "name": "meta_description",
            "type": "string",
            "required": false,
            "description": "SEO meta description for the published content",
            "default": null
          },
          {
            "name": "meta_title",
            "type": "string",
            "required": false,
            "description": "SEO meta title for the published content",
            "default": null
          },
          {
            "name": "scheduled_at",
            "type": "string",
            "required": false,
            "description": "ISO 8601 datetime to schedule publishing (e.g. '2025-01-15T10:00:00Z'). Omit for immediate publish.",
            "default": null
          },
          {
            "name": "story_excerpt",
            "type": "string",
            "required": false,
            "description": "Excerpt/summary for the published story",
            "default": null
          },
          {
            "name": "story_slug",
            "type": "string",
            "required": false,
            "description": "URL slug for the published story (e.g. 'my-blog-post')",
            "default": null
          },
          {
            "name": "story_title",
            "type": "string",
            "required": false,
            "description": "Title for the published story (defaults to document title if omitted)",
            "default": null
          },
          {
            "name": "tag_ids",
            "type": "array<['string', 'number']>",
            "required": false,
            "description": "Array of tag IDs on the target platform",
            "default": null
          },
          {
            "name": "x_thread",
            "type": "boolean",
            "required": false,
            "description": "For X (Twitter) only: publish as a thread instead of a single post",
            "default": null
          }
        ]
      },
      {
        "name": "update_document",
        "description": "Update a document's title, content, or workflow status.",
        "requiresConfirmation": true,
        "plan": null,
        "params": [
          {
            "name": "document_id",
            "type": "string",
            "required": true,
            "description": "The slug ID of the document to update",
            "default": null
          },
          {
            "name": "content",
            "type": "string",
            "required": false,
            "description": "Updated HTML content",
            "default": null
          },
          {
            "name": "status",
            "type": "string (not_started, in_progress, in_review, approved, published)",
            "required": false,
            "description": "Updated workflow status",
            "default": null
          },
          {
            "name": "title",
            "type": "string",
            "required": false,
            "description": "Updated title",
            "default": null
          }
        ]
      }
    ]
  },
  {
    "id": "planning",
    "label": "Campaigns & briefs",
    "blurb": "Plan campaigns, briefs and headline options.",
    "tools": [
      {
        "name": "create_brief",
        "description": "Create a content brief for a campaign. A brief defines a piece of content to be written \u2014 title, description, content type, keywords, deadline, etc. Use project_id to add the brief to a campaign.",
        "requiresConfirmation": false,
        "plan": null,
        "params": [
          {
            "name": "content_type",
            "type": "string (Blog Post, Youtube Script, Podcast Episode, Case Study, Quiz, Guide)",
            "required": true,
            "description": "Type of content to produce",
            "default": null
          },
          {
            "name": "description",
            "type": "string",
            "required": true,
            "description": "What the content should cover",
            "default": null
          },
          {
            "name": "title",
            "type": "string",
            "required": true,
            "description": "Brief title",
            "default": null
          },
          {
            "name": "deadline_date",
            "type": "string",
            "required": false,
            "description": "Deadline in YYYY-MM-DD format",
            "default": null
          },
          {
            "name": "keywords",
            "type": "array<string>",
            "required": false,
            "description": "Target keywords",
            "default": null
          },
          {
            "name": "language",
            "type": "string",
            "required": false,
            "description": "Language code",
            "default": "en-US"
          },
          {
            "name": "priority",
            "type": "string (low, medium, high)",
            "required": false,
            "description": "",
            "default": "medium"
          },
          {
            "name": "project_id",
            "type": "string",
            "required": false,
            "description": "Campaign/project slug to associate with",
            "default": null
          },
          {
            "name": "target_audience",
            "type": "string",
            "required": false,
            "description": "Target audience description",
            "default": null
          },
          {
            "name": "word_count",
            "type": "number",
            "required": false,
            "description": "Target word count",
            "default": null
          }
        ]
      },
      {
        "name": "create_campaign",
        "description": "Create a new campaign (stored as a 'project' internally). A campaign groups content briefs and documents under a brand with goals, audience, content types, and date range. After creating a campaign, use generate_headlines to create content ideas, then create_brief to save them as briefs. Use list_brands first to get the brand_profile_id.",
        "requiresConfirmation": true,
        "plan": null,
        "params": [
          {
            "name": "brand_profile_id",
            "type": "number",
            "required": true,
            "description": "Brand profile ID to associate with (use list_brands to find it)",
            "default": null
          },
          {
            "name": "description",
            "type": "string",
            "required": true,
            "description": "Campaign description \u2014 what this campaign is about",
            "default": null
          },
          {
            "name": "name",
            "type": "string",
            "required": true,
            "description": "Campaign name",
            "default": null
          },
          {
            "name": "campaign_goal",
            "type": "string (Lead Generation, Brand Awareness, Engagement, Sales Conversion)",
            "required": false,
            "description": "Primary goal of the campaign",
            "default": null
          },
          {
            "name": "content_types",
            "type": "string",
            "required": false,
            "description": "Comma-separated content types: Blog Post, Youtube Script, Podcast Episode, Case Study, Guide, Press Release, Email Newsletter, Instagram Carousel, Instagram Post, Instagram Reel, Linkedin Post, Linkedin Article, Twitter Thread, Medium Article, Facebook Post, TikTok Video",
            "default": "Blog Post, Linkedin Post, Email Newsletter, Instagram Post, Twitter Thread"
          },
          {
            "name": "end_date",
            "type": "string",
            "required": false,
            "description": "Campaign end date (YYYY-MM-DD)",
            "default": null
          },
          {
            "name": "key_message",
            "type": "string",
            "required": false,
            "description": "Core message or theme of the campaign",
            "default": null
          },
          {
            "name": "language",
            "type": "string",
            "required": false,
            "description": "Content language code (e.g., en-US, fr, es, de)",
            "default": "en-US"
          },
          {
            "name": "start_date",
            "type": "string",
            "required": false,
            "description": "Campaign start date (YYYY-MM-DD)",
            "default": null
          },
          {
            "name": "supporting_context",
            "type": "string",
            "required": false,
            "description": "Reference material, notes, or context documents for the campaign",
            "default": null
          },
          {
            "name": "target_audience",
            "type": "string",
            "required": false,
            "description": "Who this campaign targets",
            "default": null
          }
        ]
      },
      {
        "name": "generate_headlines",
        "description": "Generate AI-powered content headlines/ideas for a campaign (project). Returns a suggested campaign title and content brief ideas with types and headlines. Use this after creating a campaign. The returned headlines are NOT saved automatically \u2014 use create_task to save the ones you want as briefs in the campaign.",
        "requiresConfirmation": false,
        "plan": null,
        "params": [
          {
            "name": "project_id",
            "type": "string",
            "required": true,
            "description": "Campaign/project slug to generate headlines for",
            "default": null
          },
          {
            "name": "amount",
            "type": "number",
            "required": false,
            "description": "Number of headlines to generate (1-30)",
            "default": 10
          },
          {
            "name": "content_types",
            "type": "array<string>",
            "required": false,
            "description": "Content types to generate for. If omitted, uses the campaign's configured types.",
            "default": null
          },
          {
            "name": "goals",
            "type": "string",
            "required": false,
            "description": "Specific focus or goals for this batch (e.g., 'focus on tax-loss harvesting strategies')",
            "default": null
          }
        ]
      },
      {
        "name": "list_briefs",
        "description": "List content briefs in a campaign. Use this whenever the user asks about briefs, content plans, or what's planned for a campaign. Pass the campaign name or project_id to see all briefs. Supports filtering by state, priority, and content type.",
        "requiresConfirmation": false,
        "plan": null,
        "params": [
          {
            "name": "page",
            "type": "number",
            "required": false,
            "description": "Page number",
            "default": 1
          },
          {
            "name": "priority",
            "type": "string",
            "required": false,
            "description": "Comma-separated priorities: low,medium,high",
            "default": null
          },
          {
            "name": "project_id",
            "type": "string",
            "required": false,
            "description": "Campaign/project slug to scope tasks to",
            "default": null
          },
          {
            "name": "search",
            "type": "string",
            "required": false,
            "description": "Search in title or description",
            "default": null
          },
          {
            "name": "state",
            "type": "string",
            "required": false,
            "description": "Comma-separated states: not_started,in_progress,in_review,approved,published",
            "default": null
          },
          {
            "name": "type",
            "type": "string (individual, shared, all)",
            "required": false,
            "description": "Filter by ownership",
            "default": "individual"
          }
        ]
      },
      {
        "name": "list_campaigns",
        "description": "List campaigns. In Vocable, campaigns are called 'projects' internally. Each campaign has briefs (tasks) and documents. Shows document/brief counts and linked brand.",
        "requiresConfirmation": false,
        "plan": null,
        "params": [
          {
            "name": "search",
            "type": "string",
            "required": false,
            "description": "Search campaigns by name",
            "default": null
          },
          {
            "name": "type",
            "type": "string (all, individual, shared)",
            "required": false,
            "description": "Filter by ownership",
            "default": "all"
          }
        ]
      },
      {
        "name": "update_brief",
        "description": "Update a content brief \u2014 change state, priority, deadline, assignee, or details.",
        "requiresConfirmation": true,
        "plan": null,
        "params": [
          {
            "name": "task_id",
            "type": "number",
            "required": true,
            "description": "ID of the task to update",
            "default": null
          },
          {
            "name": "assignee_id",
            "type": "number",
            "required": false,
            "description": "User ID to assign the task to",
            "default": null
          },
          {
            "name": "deadline_date",
            "type": "string",
            "required": false,
            "description": "Updated deadline (YYYY-MM-DD)",
            "default": null
          },
          {
            "name": "description",
            "type": "string",
            "required": false,
            "description": "",
            "default": null
          },
          {
            "name": "priority",
            "type": "string (low, medium, high)",
            "required": false,
            "description": "Updated priority",
            "default": null
          },
          {
            "name": "state",
            "type": "string (not_started, in_progress, in_review, approved, published, scheduled)",
            "required": false,
            "description": "Updated workflow state",
            "default": null
          },
          {
            "name": "title",
            "type": "string",
            "required": false,
            "description": "",
            "default": null
          }
        ]
      }
    ]
  },
  {
    "id": "intelligence",
    "label": "Brand intelligence",
    "blurb": "Search your library, research keywords and read brand insights.",
    "tools": [
      {
        "name": "find_similar_content",
        "description": "Find content similar to a specific item in the brand's embedding space. Useful for discovering related content, avoiding duplication, or finding voice-matched examples.",
        "requiresConfirmation": false,
        "plan": null,
        "params": [
          {
            "name": "embeddable_id",
            "type": "number",
            "required": true,
            "description": "ID of the source item",
            "default": null
          },
          {
            "name": "embeddable_type",
            "type": "string (ContentSample, ContentGuideline, CustomerPersona, BrandKnowledgeBase)",
            "required": true,
            "description": "Type of the source item",
            "default": null
          },
          {
            "name": "limit",
            "type": "number",
            "required": false,
            "description": "Max similar items to return (1-20)",
            "default": 5
          }
        ]
      },
      {
        "name": "get_brand_insights",
        "description": "Get the brand insights dashboard \u2014 voice personality, tone & style, signature phrases, hook patterns, CTA style, content pillars, top performers, and cross-platform analysis. This is the same data shown on the Brand Insights page in the Vocable web app. Use this whenever the user asks about brand insights, brand analysis, or brand voice analysis.",
        "requiresConfirmation": false,
        "plan": null,
        "params": []
      },
      {
        "name": "keyword_research",
        "description": "Research keywords using Semrush \u2014 search volume, CPC, competition, trends, and related keywords. Supports up to 3 keywords for comparison.",
        "requiresConfirmation": false,
        "plan": null,
        "params": [
          {
            "name": "phrase",
            "type": "string",
            "required": true,
            "description": "Primary keyword or phrase to research",
            "default": null
          },
          {
            "name": "additional_phrases",
            "type": "array<string>",
            "required": false,
            "description": "Up to 2 more phrases for comparison",
            "default": null
          }
        ]
      },
      {
        "name": "semantic_search",
        "description": "Search across the brand's content samples, guidelines, personas, and knowledge base using AI-powered semantic search (vector embeddings). Returns ranked results with similarity scores.",
        "requiresConfirmation": false,
        "plan": null,
        "params": [
          {
            "name": "query",
            "type": "string",
            "required": true,
            "description": "Natural language search query (e.g., 'how we talk about pricing')",
            "default": null
          },
          {
            "name": "limit",
            "type": "number",
            "required": false,
            "description": "Max results to return (1-20)",
            "default": 7
          },
          {
            "name": "similarity_threshold",
            "type": "number",
            "required": false,
            "description": "Min cosine similarity (0-1). Lower = more results but less relevant",
            "default": 0.7
          }
        ]
      }
    ]
  },
  {
    "id": "training",
    "label": "Training & voice DNA",
    "blurb": "Feed Vocable your existing content so it learns your voice.",
    "tools": [
      {
        "name": "analyze_voice_dna",
        "description": "Run Voice DNA analysis on the brand's approved content samples. Requires at least 5 approved samples. Analyzes writing patterns, tone, vocabulary, and style to build a voice fingerprint.",
        "requiresConfirmation": true,
        "plan": null,
        "params": []
      },
      {
        "name": "auto_import_blogs",
        "description": "Automatically discover and import blog posts from a brand's website in one step. Combines discover + import. Scrapes the website for blog posts, then imports them as content samples.",
        "requiresConfirmation": false,
        "plan": null,
        "params": [
          {
            "name": "website_url",
            "type": "string",
            "required": true,
            "description": "The brand's website URL to discover and import blog posts from",
            "default": null
          },
          {
            "name": "max_posts",
            "type": "number",
            "required": false,
            "description": "Maximum number of blog posts to import (1-50, default 10)",
            "default": 10
          }
        ]
      },
      {
        "name": "discover_blogs",
        "description": "Discover blog posts from a brand's website URL. Returns a list of found blog post URLs and titles that can then be imported as content samples using import_blogs.",
        "requiresConfirmation": false,
        "plan": null,
        "params": [
          {
            "name": "website_url",
            "type": "string",
            "required": true,
            "description": "The brand's website URL to discover blog posts from",
            "default": null
          },
          {
            "name": "max_posts",
            "type": "number",
            "required": false,
            "description": "Maximum number of blog posts to discover (1-100, default 20)",
            "default": 20
          }
        ]
      },
      {
        "name": "fetch_social_posts",
        "description": "Fetch latest posts from a social platform and save them as content samples for brand training. Supports X/Twitter, LinkedIn, Instagram, YouTube, TikTok. Twitter/Instagram/TikTok use username, LinkedIn uses profile_url, YouTube uses channel_url or handle.",
        "requiresConfirmation": false,
        "plan": null,
        "params": [
          {
            "name": "platform",
            "type": "string (twitter, linkedin, instagram, youtube, tiktok)",
            "required": true,
            "description": "Social platform to fetch from",
            "default": null
          },
          {
            "name": "brand_profile_id",
            "type": "number",
            "required": false,
            "description": "Brand profile ID to save posts as content samples. If omitted, uses the default brand.",
            "default": null
          },
          {
            "name": "channel_url",
            "type": "string",
            "required": false,
            "description": "Channel URL or handle (for youtube \u2014 e.g. @channelname or full URL)",
            "default": null
          },
          {
            "name": "limit",
            "type": "number",
            "required": false,
            "description": "Number of posts to fetch (1-100, default 20)",
            "default": 20
          },
          {
            "name": "profile_url",
            "type": "string",
            "required": false,
            "description": "Profile URL (for linkedin \u2014 full LinkedIn profile URL)",
            "default": null
          },
          {
            "name": "username",
            "type": "string",
            "required": false,
            "description": "Username or handle (for twitter, instagram, tiktok). Include @ or not.",
            "default": null
          }
        ]
      },
      {
        "name": "import_blogs",
        "description": "Import discovered blog posts as content samples for brand training. Use discover_blogs first to find available posts, then pass their URLs here.",
        "requiresConfirmation": false,
        "plan": null,
        "params": [
          {
            "name": "urls",
            "type": "array<string>",
            "required": true,
            "description": "Array of blog post URLs to import as content samples",
            "default": null
          }
        ]
      },
      {
        "name": "list_content_samples",
        "description": "List all content samples used for brand training, with their type, approval status, and quality scores. Optionally filter by content type.",
        "requiresConfirmation": false,
        "plan": null,
        "params": [
          {
            "name": "content_type",
            "type": "string",
            "required": false,
            "description": "Filter by content type (e.g., blog_post, social_post, video_script, email)",
            "default": null
          },
          {
            "name": "page",
            "type": "number",
            "required": false,
            "description": "Page number for pagination",
            "default": null
          }
        ]
      },
      {
        "name": "run_full_analysis",
        "description": "Run a full brand analysis across all connected platforms -- voice fingerprint, hook patterns, CTA patterns, content pillars, top performers. Requires social posts to be fetched first.",
        "requiresConfirmation": true,
        "plan": null,
        "params": [
          {
            "name": "force_refresh",
            "type": "boolean",
            "required": false,
            "description": "Force re-analysis even if cached results exist",
            "default": false
          }
        ]
      },
      {
        "name": "upload_content_sample",
        "description": "Upload a text content sample for brand voice training. Provide the content text and its type. The sample will be saved and used for voice analysis and content generation.",
        "requiresConfirmation": false,
        "plan": null,
        "params": [
          {
            "name": "content",
            "type": "string",
            "required": true,
            "description": "The actual content text to upload as a training sample",
            "default": null
          },
          {
            "name": "content_type",
            "type": "string (blog_post, social_post, email, landing_page, video_script, podcast_script)",
            "required": true,
            "description": "Type of content being uploaded",
            "default": null
          },
          {
            "name": "platform",
            "type": "string",
            "required": false,
            "description": "Platform the content is from (e.g., twitter, linkedin, blog)",
            "default": null
          },
          {
            "name": "tags",
            "type": "array<string>",
            "required": false,
            "description": "Tags for categorizing the content sample",
            "default": null
          },
          {
            "name": "title",
            "type": "string",
            "required": false,
            "description": "Title for the content sample",
            "default": null
          }
        ]
      }
    ]
  },
  {
    "id": "collaboration",
    "label": "Collaboration",
    "blurb": "Share brands and documents, and see who is on the team.",
    "tools": [
      {
        "name": "add_campaign_collaborator",
        "description": "Add a collaborator to a campaign/project. If the user is not yet on the platform, an invitation email will be sent.",
        "requiresConfirmation": true,
        "plan": null,
        "params": [
          {
            "name": "project_id",
            "type": "string",
            "required": true,
            "description": "Campaign/project slug to add the collaborator to",
            "default": null
          },
          {
            "name": "email",
            "type": "string",
            "required": false,
            "description": "Email address to invite (provide either user_id or email)",
            "default": null
          },
          {
            "name": "user_id",
            "type": "number",
            "required": false,
            "description": "User ID of an existing team member to add (provide either user_id or email)",
            "default": null
          }
        ]
      },
      {
        "name": "list_collaborators",
        "description": "List collaborators on a document, showing who has access and their permission level.",
        "requiresConfirmation": false,
        "plan": null,
        "params": [
          {
            "name": "document_id",
            "type": "string",
            "required": true,
            "description": "Document slug/ID to check collaborators for",
            "default": null
          }
        ]
      },
      {
        "name": "list_team_members",
        "description": "List all team members across your teams, showing names, emails, and roles.",
        "requiresConfirmation": false,
        "plan": null,
        "params": []
      },
      {
        "name": "share_brand",
        "description": "Share a brand with a team member by adding them as a collaborator. The user must be in your team.",
        "requiresConfirmation": true,
        "plan": null,
        "params": [
          {
            "name": "business_id",
            "type": "number",
            "required": true,
            "description": "The business/brand ID to share",
            "default": null
          },
          {
            "name": "user_id",
            "type": "number",
            "required": true,
            "description": "The user ID of the team member to grant access",
            "default": null
          }
        ]
      },
      {
        "name": "share_document",
        "description": "Share a document with a collaborator by email. Sends an invitation granting the specified access level.",
        "requiresConfirmation": true,
        "plan": null,
        "params": [
          {
            "name": "access_level",
            "type": "string (Full Access, Can Edit, Can Comment, Can View)",
            "required": true,
            "description": "Permission level to grant the collaborator",
            "default": null
          },
          {
            "name": "document_id",
            "type": "string",
            "required": true,
            "description": "Document slug/ID to share",
            "default": null
          },
          {
            "name": "email",
            "type": "string",
            "required": true,
            "description": "Email address of the collaborator to invite",
            "default": null
          }
        ]
      }
    ]
  },
  {
    "id": "publishing",
    "label": "Publishing",
    "blurb": "Where your published content can go.",
    "tools": [
      {
        "name": "list_integrations",
        "description": "List all connected publishing platforms (WordPress, Webflow, Wix, LinkedIn, X, Facebook). Use this to find integration IDs for the publish_document tool.",
        "requiresConfirmation": false,
        "plan": "Pro+",
        "params": []
      }
    ]
  },
  {
    "id": "system",
    "label": "System",
    "blurb": "Operational helpers.",
    "tools": [
      {
        "name": "rate_limit_status",
        "description": "Check current rate limit usage across all tools. Shows remaining capacity per minute/hour/day.",
        "requiresConfirmation": false,
        "plan": null,
        "params": []
      }
    ]
  }
];

export const MCP_RESOURCES = [
  {
    "name": "brand-profile",
    "uri": "vocable://brand/profile",
    "description": "The brand's core identity \u2014 name, industry, mission, values, audience, and messaging. Read this first before any brand-aware operation."
  },
  {
    "name": "voice-dna",
    "uri": "vocable://brand/voice-dna",
    "description": "Deep analysis of the brand's writing voice \u2014 tone, vocabulary, sentence structure, stylistic patterns, and confidence score. Read this before generating content to match the brand voice precisely."
  },
  {
    "name": "content-guidelines",
    "uri": "vocable://brand/guidelines",
    "description": "Prescriptive writing rules \u2014 voice characteristics, tone, style rules, preferred/avoided vocabulary, dos and don'ts, grammar style, emoji/contraction usage. This is the style guide that tells you exactly how to write for this brand."
  },
  {
    "name": "audience-personas",
    "uri": "vocable://brand/personas",
    "description": "Target audience personas with demographics, pain points, goals, interests, and preferred channels. Use this to tailor content tone and messaging."
  },
  {
    "name": "content-strategy",
    "uri": "vocable://brand/content-strategy",
    "description": "The brand's overall content strategy \u2014 primary/secondary tones, target audiences, content goals, topics to cover/avoid, CTA style, emoji and hashtag usage. Read this when planning content calendars or setting campaign direction."
  },
  {
    "name": "content-samples",
    "uri": "vocable://brand/content-samples",
    "description": "Approved content samples that exemplify the brand's voice across different types (blog, social, email, video scripts, etc.). Use these as reference material when generating new content."
  },
  {
    "name": "social-channels",
    "uri": "vocable://brand/social-channels",
    "description": "Connected social media channels \u2014 platforms, handles, activity status, and post counts. Shows which channels the brand uses for content distribution."
  },
  {
    "name": "brand-insights",
    "uri": "vocable://brand/insights",
    "description": "Brand intelligence insights \u2014 unified voice analysis, content pillars, top performers, and cross-platform patterns. Read this to understand what content works best for the brand."
  },
  {
    "name": "training-status",
    "uri": "vocable://brand/training-status",
    "description": "Brand intelligence training status \u2014 content samples count, voice DNA readiness, content strategy state, and training exercises completed. Check this to understand how well-trained the brand intelligence is."
  }
];

export const TOOL_COUNT = TOOL_CATEGORIES.reduce((n, c) => n + c.tools.length, 0);
export const RESOURCE_COUNT = MCP_RESOURCES.length;
